import React, { useState, useRef } from 'react'
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Switch,
  FormControlLabel,
  useMediaQuery,
  useTheme,
  CircularProgress
} from '@mui/material'
import {
  MenuBook as VocabIcon,
  Search as SearchIcon,
  Lightbulb as LightbulbIcon,
  Translate as TranslateIcon,
  Speed as StreamIcon
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import { apiClient, API_ENDPOINTS, buildApiUrl } from '../config/api'
import toast from 'react-hot-toast'
import ReactMarkdown from 'react-markdown'


const vocabularyTopics = [
  { title: 'Business English', description: 'Từ vựng kinh doanh và công việc' },
  { title: 'Academic English', description: 'Từ vựng học thuật và giáo dục' },
  { title: 'Daily Conversation', description: 'Từ vựng hàng ngày' },
  { title: 'Travel & Tourism', description: 'Từ vựng du lịch' },
  { title: 'Technology', description: 'Từ vựng công nghệ' },
  { title: 'Health & Medicine', description: 'Từ vựng y tế và sức khỏe' }
]

function Vocabulary() {
  const [searchWord, setSearchWord] = useState('')
  const [selectedTopic, setSelectedTopic] = useState('')
  const [vocabularyHelp, setVocabularyHelp] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [studentLevel, setStudentLevel] = useState('B1')
  const [streamingEnabled, setStreamingEnabled] = useState(true)
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingFeedback, setStreamingFeedback] = useState('')
  const eventSourceRef = useRef(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleSearchWord = async () => {
    if (!searchWord.trim()) {
      toast.error('Vui lòng nhập từ cần tra cứu')
      return
    }

    if (streamingEnabled) {
      await handleStreamingVocabulary()
    } else {
      await handleRegularVocabulary()
    }
  }

  const handleRegularVocabulary = async () => {
    setIsLoading(true)
    try {
      const response = await apiClient.post(API_ENDPOINTS.TEACHER.VOCABULARY, {
        word: searchWord,
        studentLevel: studentLevel,
        context: selectedTopic
      })
      
      setVocabularyHelp(response.data)
      toast.success('Đã tìm thấy thông tin từ vựng!')
    } catch (error) {
      console.error('Vocabulary search error:', error)
      toast.error('Có lỗi xảy ra khi tra cứu từ vựng')
    } finally {
      setIsLoading(false)
    }
  }

  const handleStreamingVocabulary = async () => {
    setIsStreaming(true)
    setStreamingFeedback('')
    setVocabularyHelp(null)
    
    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.TEACHER.VOCABULARY_STREAM), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          word: searchWord,
          studentLevel: studentLevel,
          context: selectedTopic
        })
      })

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let fullContent = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            
            if (data === '[DONE]') continue
            
            try {
              const parsed = JSON.parse(data)
              
              if (parsed.type === 'chunk' && parsed.content) {
                fullContent += parsed.content
                setStreamingFeedback(fullContent)
              } else if (parsed.type === 'complete') {
                const completeHelp = {
                  word: searchWord,
                  explanation: parsed.fullContent || fullContent,
                  timestamp: new Date().toISOString()
                }
                setVocabularyHelp(completeHelp)
                toast.success('Đã tìm thấy thông tin từ vựng!')
              } else if (parsed.type === 'error') {
                throw new Error(parsed.error)
              }
            } catch (parseError) {
              console.error('Parse error:', parseError)
            }
          }
        }
      }
    } catch (error) {
      console.error('Streaming vocabulary error:', error)
      toast.error('Có lỗi xảy ra khi tra cứu từ vựng')
    } finally {
      setIsStreaming(false)
      setStreamingFeedback('')
    }
  }

  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic.title)
    setSearchWord('')
    setVocabularyHelp(null)
  }

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearchWord()
    }
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <VocabIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h3" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
            Từ vựng
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Tra cứu và học từ vựng tiếng Anh với sự hỗ trợ của AI
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Topics and Search */}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <LightbulbIcon sx={{ mr: 1 }} />
                Chủ đề từ vựng
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {vocabularyTopics.map((topic, index) => (
                  <Card 
                    key={index}
                    sx={{ 
                      cursor: 'pointer',
                      border: selectedTopic === topic.title ? 2 : 1,
                      borderColor: selectedTopic === topic.title ? 'primary.main' : 'divider',
                      '&:hover': { borderColor: 'primary.main' }
                    }}
                    onClick={() => handleTopicSelect(topic)}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {topic.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {topic.description}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Paper>

            {/* Search Box */}
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <SearchIcon sx={{ mr: 1 }} />
                Tra cứu từ
              </Typography>
              {selectedTopic && (
                <Chip 
                  label={`Chủ đề: ${selectedTopic}`} 
                  color="primary" 
                  size="small" 
                  sx={{ mb: 2 }}
                />
              )}
              <TextField
                fullWidth
                placeholder="Nhập từ cần tra cứu..."
                value={searchWord}
                onChange={(e) => setSearchWord(e.target.value)}
                onKeyPress={handleKeyPress}
                sx={{ mb: 2 }}
              />
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={streamingEnabled}
                      onChange={(e) => setStreamingEnabled(e.target.checked)}
                      size={isMobile ? 'small' : 'medium'}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <StreamIcon fontSize="small" />
                      <Typography variant="body2">Streaming</Typography>
                    </Box>
                  }
                />
              </Box>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSearchWord}
                disabled={isLoading || isStreaming || !searchWord.trim()}
                startIcon={isStreaming ? <CircularProgress size={16} /> : <TranslateIcon />}
              >
                {isStreaming ? 'Đang tra cứu...' : isLoading ? 'Đang tra cứu...' : 'Tra cứu'}
              </Button>
            </Paper>
          </Grid>

          {/* Results */}
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 3, minHeight: '60vh' }}> 
              {vocabularyHelp && console.log(vocabularyHelp)}
              
              {/* Streaming Feedback */}
              {isStreaming && streamingFeedback && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Đang tra cứu từ: <strong>{searchWord}</strong>
                    </Box>
                  </Alert>
                  <Box sx={{ 
                    '& p': { mb: 1 },
                    '& ul, & ol': { pl: 2, mb: 1 },
                    '& li': { mb: 0.5 },
                    '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 2, mb: 1 },
                    '& blockquote': { borderLeft: '4px solid #ccc', pl: 2, fontStyle: 'italic' },
                    '& code': { bgcolor: 'grey.100', px: 0.5, py: 0.25, borderRadius: 0.5 },
                    '& pre': { bgcolor: 'grey.100', p: 1, borderRadius: 1, overflow: 'auto' }
                  }}>
                    <ReactMarkdown>{streamingFeedback}</ReactMarkdown>
                  </Box>
                </motion.div>
              )}
              
              {vocabularyHelp ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <Alert severity="success" sx={{ mb: 3 }}>
                    Đã tìm thấy thông tin cho từ: <strong>{searchWord}</strong>
                  </Alert>
                  <ReactMarkdown>{vocabularyHelp.explanation}</ReactMarkdown>
                </motion.div>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <VocabIcon sx={{ fontSize: 80, color: 'grey.300', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                    Chọn chủ đề và nhập từ để tra cứu
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    AI sẽ cung cấp định nghĩa, cách sử dụng, ví dụ và từ đồng nghĩa
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Tips */}
        <Paper elevation={2} sx={{ p: 3, mt: 4, bgcolor: 'primary.50' }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            💡 Mẹo học từ vựng hiệu quả
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Học từ trong ngữ cảnh thay vì học thuộc lòng" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Tạo câu ví dụ với từ mới để ghi nhớ lâu hơn" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Ôn tập thường xuyên và sử dụng từ trong giao tiếp" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Học từ đồng nghĩa và trái nghĩa để mở rộng vốn từ" />
            </ListItem>
          </List>
        </Paper>
      </motion.div>
    </Container>
  )
}

export default Vocabulary